/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import DataActionBodyAttributeRow from 'cio/app-models/data-action/body-attribute-row';
import { inject as service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import RouterService from '@ember/routing/router-service';
import AnalyticsService from 'cio/services/analytics';
import { DataActionComposerSectionType, DataActionConditionType } from 'cio/utils/campaigns/data-action';
import { Select } from 'ember-power-select/components/power-select';
import EnvironmentService from 'cio/services/environment';
import CampaignModel from 'cio/models/campaign';

interface DataActionDefaultBodyEditorRowArgs {
  campaign: CampaignModel;
  supportedConditionTypes: DataActionConditionType[];
  row: DataActionBodyAttributeRow;
  rows: DataActionBodyAttributeRow[];
  index: number;
  removeRow: () => unknown;
  canRemoveRow: boolean;
  autocompleteValues: Partial<Record<DataActionConditionType, { label: string; value: string; path: string[] }[]>>;
  nameAutocompleteValues?: string[];
  showNameAutocompleteInput?: boolean;
  updateHighlight: (highlight: Select['highlighted']) => unknown;
  isLoadingAsyncAutocompleteValues: boolean;
  liquidFieldFocusIn: (fieldName: string, index?: number) => unknown;
  liquidFieldFocusOut: () => unknown;
  previewDidChange?: () => unknown;
  showLiquidTypeFallbackNotice?: boolean;
  hasReadOnlyPermission: boolean;
}

export default class DataActionDefaultBodyEditorRow extends Component<DataActionDefaultBodyEditorRowArgs> {
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service router!: RouterService;

  DataActionConditionType = DataActionConditionType;
  DataActionComposerSectionType = DataActionComposerSectionType;

  @cached
  get otherRows() {
    return this.args.rows.filter((_, index) => index !== this.args.index);
  }

  get errors() {
    let errors = { ...this.args.row.errors };

    if (this.isRowNameDuplicate()) {
      let error = 'Name must be unique';
      if (errors.name != null) {
        errors.name.push(error);
      } else {
        errors.name = [error];
      }
    }

    return errors;
  }

  private isRowNameDuplicate() {
    return !isEmpty(this.args.row.name) && this.otherRows.some((row) => row.name.trim() === this.args.row.name.trim());
  }

  private isFirstRowWithDuplicateName() {
    let firstDuplicateRowIndex = this.otherRows.findIndex((row) => row.name.trim() === this.args.row.name.trim());

    return firstDuplicateRowIndex === this.args.index;
  }

  @action
  onChangeType(type: DataActionConditionType) {
    this.args.row.type = type;
  }

  @action
  onChangeValue(value: string) {
    this.args.row.value = value;
  }

  trackedErrors: string[] = [];

  @action
  trackErrorChange(_: HTMLElement, [errors]: [string[]?]) {
    if (errors != null && errors.length > 0) {
      let [error] = errors;

      if (error.includes('must be unique') && !this.isFirstRowWithDuplicateName()) {
        // Only track the duplicate name error for one row - otherwise it would
        // be tracked multiple times
        return;
      }

      if (!this.trackedErrors.includes(error)) {
        this.trackedErrors = [...this.trackedErrors, error];

        this.analytics.track('user', 'error_encountered', {
          currentUrl: this.router.currentURL,
          message: error,
          source: 'Create Event action',
        });
      }
    }
  }
}
